import React, { useCallback, useMemo } from 'react'
import { Keyboard } from 'react-native'
import { useRouter } from 'expo-router'
import type { ChatMessageResponse, ChatReactionEmoji } from '@warcarp/api-types'
import { usePostMessage, useToggleChatReaction } from '../../hooks/useChat'
import { useToast } from '../ui/Toast'
import { pickFirstUnreadIndex } from '../../lib/chatHelpers'
import { GAME_TOP_BAR_HEIGHT } from '../../lib/gameChrome'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import type { GameState } from '../../lib/gameState'
import { MessageThread, type ThreadMessage } from './MessageThread'
import { ScreenDrawer } from './ScreenDrawer'

interface GameChatDrawerProps {
  gameId: string
  state: GameState
  visible: boolean
  navBarBottom: number | null
  messages: ChatMessageResponse[]
  focusMessageId?: string
  currentUserId?: string
  sessionSeenId: string | null | undefined
  onSeen: () => void
  onClose: () => void
}

/** Existing game chat conversation rendered inside the shared game drawer. */
export function GameChatDrawer({
  gameId,
  state,
  visible,
  navBarBottom,
  messages: rawMessages,
  focusMessageId,
  currentUserId,
  sessionSeenId,
  onSeen,
  onClose,
}: GameChatDrawerProps) {
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const { showToast } = useToast()
  const { isLandscape, actionPanelWidth } = useMapDimensions(state)
  const trayLeft = isLandscape ? actionPanelWidth : 0
  const trayTop = isLandscape
    ? insets.top
    : navBarBottom ?? insets.top + GAME_TOP_BAR_HEIGHT
  const postMessage = usePostMessage(gameId)
  const toggleReaction = useToggleChatReaction(gameId)

  const messages = useMemo<ThreadMessage[]>(
    () => rawMessages.map((message) => ({
      id: message.id,
      authorId: message.userId,
      authorName: message.displayName,
      content: message.content,
      createdAt: message.createdAt,
      reactions: message.reactions ?? [],
    })),
    [rawMessages],
  )

  const handleSend = useCallback(async (content: string) => {
    await postMessage.mutateAsync({ content })
  }, [postMessage])

  const handleReact = useCallback((messageId: string, emoji: ChatReactionEmoji) => {
    toggleReaction.mutate(
      { messageId, emoji },
      { onError: () => showToast("Couldn't update reaction — please try again.", 'error') },
    )
  }, [showToast, toggleReaction])

  const closeFromOverlay = useCallback(() => {
    Keyboard.dismiss()
    onSeen()
    onClose()
  }, [onClose, onSeen])
  const closeFromThread = useCallback(() => {
    Keyboard.dismiss()
    onClose()
  }, [onClose])

  return (
    <ScreenDrawer
      visible={visible}
      onClose={closeFromOverlay}
      top={trayTop}
      left={trayLeft}
      slideFrom="bottom"
      animateExitOnWeb
      accessibilityLabel="Game chat"
      backdropLabel="Dismiss chat drawer"
      backdropTestID="game-chat-backdrop"
      testID="game-chat-drawer"
    >
      {() => (
        <MessageThread
          title="Chat"
          messages={messages}
          currentUserId={currentUserId}
          onSend={handleSend}
          isSending={postMessage.isPending}
          onClose={closeFromThread}
          firstUnreadIndex={pickFirstUnreadIndex(rawMessages, sessionSeenId)}
          onSeen={onSeen}
          onReact={handleReact}
          onReport={(message) => router.push({
            pathname: '/(app)/settings/blocked-users',
            params: { block: message.authorId, messageId: message.id, kind: 'chat' },
          })}
          reactingMessageId={toggleReaction.isPending ? toggleReaction.variables?.messageId : undefined}
          focusMessageId={focusMessageId}
          emptyIcon="💬"
          emptyText="No messages yet. Break the silence."
          placeholder="Message…"
          domIdPrefix="game-chat-drawer"
          presentation="drawer"
          isActive={visible}
          keyboardVerticalOffset={trayTop}
        />
      )}
    </ScreenDrawer>
  )
}
